import { getAssessmentSignatureMetadata } from '../../../utils/documentVerification';
import { ASSESSMENT_REPORT_TEST_KEY } from '../reports/assessmentReportCatalogs';
import {
  countAssessmentReportFindings,
  getAssessmentReportPeriod,
  normalizeAssessmentReportPayload,
  validateAssessmentReportPayload,
  type AssessmentReportPayloadV2,
} from '../reports/assessmentReportDomain';
import { isAssessmentV2ReportKey } from '../registry/assessmentRegistry';
import type { AssessmentResultV2 } from '../types';
import {
  AssessmentResultsV2Service,
  type AssessmentResultV2Revision,
} from './AssessmentResultsV2Service';
import { AssessmentSessionsV2Service } from './AssessmentSessionsV2Service';

const ASSESSMENT_REPORT_TEST_VERSION = 2;

export type AssessmentReportPatientPersistenceSnapshot = Pick<
  AssessmentResultV2,
  | 'patientNameSnapshot'
  | 'patientMedicalRecordSnapshot'
  | 'patientBirthDateSnapshot'
  | 'patientSexSnapshot'
  | 'patientWeightSnapshot'
  | 'patientHeightSnapshot'
>;

export type AssessmentReportProfessionalPersistenceSnapshot = Pick<
  AssessmentResultV2,
  | 'professionalNameSnapshot'
  | 'professionalRegistrationSnapshot'
  | 'professionalRoleSnapshot'
>;

export interface AssessmentReportPersistenceInput {
  notes?: string | null;
  professionalUserId?: string | null;
  patientSnapshot: AssessmentReportPatientPersistenceSnapshot;
  professionalSnapshot: AssessmentReportProfessionalPersistenceSnapshot;
  summaryJson: Record<string, unknown>;
  payloadJson: AssessmentReportPayloadV2;
  assetsJson: Record<string, unknown>;
}

export interface CreateAssessmentReportInput extends AssessmentReportPersistenceInput {
  clientId: string;
  patientId: string;
  assessmentDate: string;
  createdByUserId?: string | null;
}

export interface UpdateAssessmentReportInput extends AssessmentReportPersistenceInput {
  expectedRevision: AssessmentResultV2Revision;
}

export class AssessmentReportV2Service {
  private static assertPayloadPatient(payload: AssessmentReportPayloadV2, patientId: string): void {
    if (payload.patient.patientId !== patientId) {
      throw new Error('ASSESSMENT_REPORT_PATIENT_MISMATCH');
    }
  }

  private static normalizeAndValidatePayload(payload: unknown): AssessmentReportPayloadV2 {
    const normalized = normalizeAssessmentReportPayload(payload);
    if (!normalized) {
      throw new Error('ASSESSMENT_REPORT_INVALID_PAYLOAD');
    }

    const validation = validateAssessmentReportPayload(normalized);
    if (!validation.valid) {
      throw new Error(validation.errors[0]?.message || 'ASSESSMENT_REPORT_INVALID_PAYLOAD');
    }

    return normalized;
  }

  private static withoutSignature(
    assetsJson: Record<string, unknown>,
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(assetsJson).filter(([key]) => key !== 'signature'),
    );
  }

  private static revisionsMatch(
    expectedUpdatedAt: string | null,
    expectedCreatedAt: string | null,
    currentUpdatedAt?: string | null,
    currentCreatedAt?: string | null,
  ): boolean {
    const expected = expectedUpdatedAt?.trim() || expectedCreatedAt?.trim() || '';
    const current = currentUpdatedAt?.trim() || currentCreatedAt?.trim() || '';
    return Boolean(expected && current && expected === current);
  }

  private static async assertReferencesAreCurrent(
    clientId: string,
    patientId: string,
    payload: AssessmentReportPayloadV2,
  ): Promise<void> {
    const sourceIds = payload.sources.map((source) => source.resultId);
    const historicalIds =
      payload.exportOptions.history.mode === 'selected'
        ? payload.exportOptions.history.selections.flatMap(
            (selection) => selection.historicalResultIds,
          )
        : [];
    const currentResults = await AssessmentResultsV2Service.listByIds(
      clientId,
      [...new Set([...sourceIds, ...historicalIds])],
    );
    const currentById = new Map(
      currentResults.map((result) => [result.resultId, result]),
    );
    const sourceById = new Map(
      payload.sources.map((source) => [source.resultId, source]),
    );

    for (const source of payload.sources) {
      const current = currentById.get(source.resultId);
      if (
        !current
        || current.clientId !== clientId
        || current.patientId !== patientId
        || current.status !== 'completed'
        || isAssessmentV2ReportKey(current.testKey)
        || current.sessionId !== source.sessionId
        || current.testKey !== source.snapshot.testKey
        || current.testVersion !== source.snapshot.testVersion
        || current.assessmentDate !== source.snapshot.assessmentDate
      ) {
        throw new Error(
          `A avaliação-fonte "${source.snapshot.testLabel}" não corresponde mais ao snapshot revisado.`,
        );
      }
      if (
        !this.revisionsMatch(
          source.snapshot.sourceUpdatedAt,
          source.snapshot.sourceCreatedAt,
          current.updatedAt,
          current.createdAt,
        )
      ) {
        throw new Error(
          `A avaliação "${source.snapshot.testLabel}" foi alterada após a seleção. Remova essa fonte e selecione-a novamente para revisar o resultado atualizado.`,
        );
      }
    }

    if (payload.exportOptions.history.mode !== 'selected') return;

    for (const selection of payload.exportOptions.history.selections) {
      const source = sourceById.get(selection.sourceResultId);
      const historicalSnapshotById = new Map(
        selection.historicalResultSnapshots.map((snapshot) => [
          snapshot.resultId,
          snapshot,
        ]),
      );

      for (const historicalResultId of selection.historicalResultIds) {
        const snapshot = historicalSnapshotById.get(historicalResultId);
        const current = currentById.get(historicalResultId);
        const historyLabel = snapshot
          ? `${snapshot.testKey} de ${new Date(snapshot.assessmentDate).toLocaleDateString('pt-BR')}`
          : historicalResultId;

        if (!snapshot) {
          throw new Error(
            `O histórico "${historyLabel}" foi salvo sem snapshot verificável. Revise e selecione-o novamente.`,
          );
        }
        if (
          !source
          || !current
          || current.clientId !== clientId
          || current.patientId !== patientId
          || current.status !== 'completed'
          || isAssessmentV2ReportKey(current.testKey)
          || current.sessionId !== snapshot.sessionId
          || current.testKey !== snapshot.testKey
          || current.testVersion !== snapshot.testVersion
          || current.assessmentDate !== snapshot.assessmentDate
          || current.testKey !== source.snapshot.testKey
          || Date.parse(current.assessmentDate) >= Date.parse(source.snapshot.assessmentDate)
        ) {
          throw new Error(
            `O histórico "${historyLabel}" não corresponde mais à seleção revisada. Remova-o e selecione-o novamente.`,
          );
        }
        if (
          !this.revisionsMatch(
            snapshot.sourceUpdatedAt,
            snapshot.sourceCreatedAt,
            current.updatedAt,
            current.createdAt,
          )
        ) {
          throw new Error(
            `O histórico "${historyLabel}" foi alterado após a seleção. Remova-o e selecione-o novamente para revisar o resultado atualizado.`,
          );
        }
      }
    }
  }

  private static buildCanonicalSummary(
    payload: AssessmentReportPayloadV2,
    summaryJson: Record<string, unknown>,
  ): Record<string, unknown> {
    const findingCounts = countAssessmentReportFindings(payload.sources);
    const period = getAssessmentReportPeriod(payload.sources);
    return {
      ...summaryJson,
      title: payload.title,
      mode: payload.mode,
      includedCount: payload.sources.length,
      alteredCount: findingCounts.altered,
      borderlineCount: findingCounts.borderline,
      withinExpectedCount: findingCounts.withinExpected,
      unresolvedCount: findingCounts.notClassified + findingCounts.notInterpretable,
      periodStart: period?.startDate ?? null,
      periodEnd: period?.endDate ?? null,
    };
  }

  static async assertReportReferencesAreCurrent(
    result: AssessmentResultV2,
  ): Promise<void> {
    if (result.testKey !== ASSESSMENT_REPORT_TEST_KEY) {
      throw new Error('ASSESSMENT_REPORT_KEY_MISMATCH');
    }

    const payload = this.normalizeAndValidatePayload(result.payloadJson);
    this.assertPayloadPatient(payload, result.patientId);
    await this.assertReferencesAreCurrent(result.clientId, result.patientId, payload);
  }

  static async create(input: CreateAssessmentReportInput): Promise<AssessmentResultV2> {
    const payloadJson = this.normalizeAndValidatePayload(input.payloadJson);
    this.assertPayloadPatient(payloadJson, input.patientId);
    if (
      payloadJson.authorship.professional.userId
      && payloadJson.authorship.professional.userId !== input.professionalUserId
    ) {
      throw new Error('ASSESSMENT_REPORT_AUTHOR_MISMATCH');
    }
    await this.assertReferencesAreCurrent(input.clientId, input.patientId, payloadJson);

    const session = await AssessmentSessionsV2Service.create({
      clientId: input.clientId,
      patientId: input.patientId,
      sessionDate: input.assessmentDate,
      notes: input.notes ?? null,
      selectedTests: [ASSESSMENT_REPORT_TEST_KEY],
      status: 'completed',
      createdByUserId: input.createdByUserId ?? null,
    });

    try {
      return await AssessmentResultsV2Service.create({
        sessionId: session.sessionId,
        clientId: input.clientId,
        patientId: input.patientId,
        testKey: ASSESSMENT_REPORT_TEST_KEY,
        testVersion: ASSESSMENT_REPORT_TEST_VERSION,
        assessmentDate: input.assessmentDate,
        groupIndex: 1,
        groupTotal: 1,
        status: 'completed',
        notes: input.notes ?? null,
        ...input.patientSnapshot,
        patientNameSnapshot: payloadJson.patient.name,
        patientMedicalRecordSnapshot: payloadJson.patient.medicalRecord,
        patientBirthDateSnapshot: payloadJson.patient.birthDate,
        professionalUserId:
          payloadJson.authorship.professional.userId
          ?? input.professionalUserId
          ?? null,
        ...input.professionalSnapshot,
        professionalNameSnapshot: payloadJson.authorship.professional.name,
        professionalRegistrationSnapshot: payloadJson.authorship.professional.registration,
        professionalRoleSnapshot: payloadJson.authorship.professional.role,
        summaryJson: this.buildCanonicalSummary(payloadJson, input.summaryJson),
        payloadJson,
        assetsJson: this.withoutSignature(input.assetsJson),
        legacySourceRecordId: null,
        createdByUserId: input.createdByUserId ?? null,
      });
    } catch (error) {
      await AssessmentSessionsV2Service.delete(session.sessionId, input.clientId).catch((cleanupError) => {
        console.error('Erro ao remover sessão órfã após falha ao criar relatório de avaliações:', cleanupError);
      });
      throw error;
    }
  }

  static async update(
    resultId: string,
    clientId: string,
    patientId: string,
    input: UpdateAssessmentReportInput,
  ): Promise<AssessmentResultV2> {
    const payloadJson = this.normalizeAndValidatePayload(input.payloadJson);
    this.assertPayloadPatient(payloadJson, patientId);
    if (
      payloadJson.authorship.professional.userId
      && payloadJson.authorship.professional.userId !== input.professionalUserId
    ) {
      throw new Error('ASSESSMENT_REPORT_AUTHOR_MISMATCH');
    }

    const [existing] = await AssessmentResultsV2Service.listByIds(clientId, [resultId]);
    if (!existing) {
      throw new Error('ASSESSMENT_REPORT_NOT_FOUND');
    }

    if (existing.testKey !== ASSESSMENT_REPORT_TEST_KEY) {
      throw new Error('ASSESSMENT_REPORT_KEY_MISMATCH');
    }

    if (existing.patientId !== patientId) {
      throw new Error('ASSESSMENT_REPORT_PATIENT_MISMATCH');
    }

    if (existing.status !== 'completed') {
      throw new Error('ASSESSMENT_REPORT_NOT_EDITABLE');
    }

    if (getAssessmentSignatureMetadata(existing.assetsJson)) {
      throw new Error('SIGNED_ASSESSMENT_RESULT_LOCKED');
    }
    if (
      !this.revisionsMatch(
        input.expectedRevision.updatedAt ?? null,
        input.expectedRevision.createdAt ?? null,
        existing.updatedAt,
        existing.createdAt,
      )
    ) {
      throw new Error('ASSESSMENT_RESULT_CONCURRENT_MODIFICATION');
    }
    const previousPayload = normalizeAssessmentReportPayload(existing.payloadJson);
    if (!previousPayload || previousPayload.mode !== payloadJson.mode) {
      throw new Error('ASSESSMENT_REPORT_MODE_IMMUTABLE');
    }
    await this.assertReferencesAreCurrent(clientId, patientId, payloadJson);

    const existingRevisionHistory = Array.isArray(
      existing.assetsJson?.reportRevisionHistory,
    )
      ? existing.assetsJson.reportRevisionHistory
      : [];
    const revisionEntry = {
      revisionNumber: existingRevisionHistory.length + 1,
      revisedAt: payloadJson.updatedAt,
      revisedBy: payloadJson.authorship.professional,
      previousAuthor: previousPayload?.authorship.professional ?? null,
      previousUpdatedAt: existing.updatedAt ?? existing.createdAt ?? null,
    };
    const assetsJson = {
      ...(existing.assetsJson && typeof existing.assetsJson === 'object' ? existing.assetsJson : {}),
      ...this.withoutSignature(input.assetsJson),
      reportRevisionHistory: [...existingRevisionHistory, revisionEntry],
    };

    const updatePayload: Parameters<typeof AssessmentResultsV2Service.update>[2] = {
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
      professionalUserId:
        payloadJson.authorship.professional.userId
        ?? input.professionalUserId
        ?? null,
      ...input.patientSnapshot,
      patientNameSnapshot: payloadJson.patient.name,
      patientMedicalRecordSnapshot: payloadJson.patient.medicalRecord,
      patientBirthDateSnapshot: payloadJson.patient.birthDate,
      ...input.professionalSnapshot,
      professionalNameSnapshot: payloadJson.authorship.professional.name,
      professionalRegistrationSnapshot: payloadJson.authorship.professional.registration,
      professionalRoleSnapshot: payloadJson.authorship.professional.role,
      summaryJson: this.buildCanonicalSummary(payloadJson, input.summaryJson),
      payloadJson,
      assetsJson,
    };

    return AssessmentResultsV2Service.updateIfRevisionMatches(
      resultId,
      clientId,
      {
        updatedAt: existing.updatedAt,
        createdAt: existing.createdAt,
      },
      updatePayload,
    );
  }
}
